import { Star as StarIcon } from '@mui/icons-material';
import { Button, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@src/Store/store';
import { Grid3X3, List } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';

import SectionFilterHeader from './SectionFilterHeader';
import SegmentLevelFilter from './SegmentLevelFilter';

interface SkillsFilterHeaderProps {
    searchValue: string;
    onSearchChange: (value: string) => void;
    selectedLevel: number | null;
    onLevelChange: (level: number | null) => void;
    showSuperPowers: boolean;
    onToggleSuperPowers: () => void;
    viewMode: 'list' | 'chips';
    onViewModeChange: (mode: 'list' | 'chips') => void;
}

function SkillsFilterHeader({
    searchValue,
    onSearchChange,
    selectedLevel,
    onLevelChange,
    showSuperPowers,
    onToggleSuperPowers,
    viewMode,
    onViewModeChange,
}: SkillsFilterHeaderProps) {
    const theme = useTheme();
    const employee = useSelector((state: AppState) => state.employee);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    const searchOptions = React.useMemo(() => {
        return (employee.skills || []).map((skill) => ({
            title: skill.title,
            id: skill.id,
        }));
    }, [employee.skills]);

    return (
        <SectionFilterHeader
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchLabel={langStrings.profile.skillsSearch}
            searchOptions={searchOptions}
        >
            {/* Level Filter Tabs */}
            <SegmentLevelFilter selectedLevel={selectedLevel} onLevelChange={onLevelChange} />

            {/* SuperPowers Button */}
            <Button
                size="small"
                variant={showSuperPowers ? 'contained' : 'outlined'}
                startIcon={<StarIcon />}
                onClick={onToggleSuperPowers}
                sx={{
                    bgcolor: showSuperPowers ? theme.palette.warning.main : 'transparent',
                    color: showSuperPowers ? 'white' : theme.palette.warning.main,
                    borderColor: theme.palette.warning.main,
                    '&:hover': {
                        bgcolor: showSuperPowers ? theme.palette.warning.dark : `${theme.palette.warning.main}15`,
                    },
                }}
            >
                {langStrings.profile.superPowers}
            </Button>

            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && onViewModeChange(newMode)}
                size="small"
                sx={{
                    '& .MuiToggleButton-root': {
                        border: `1px solid ${theme.palette.primary.main}40`,
                        '&.Mui-selected': {
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                        },
                    },
                }}
            >
                <ToggleButton value="chips">
                    <Grid3X3 size={16} />
                </ToggleButton>
                <ToggleButton value="list">
                    <List size={16} />
                </ToggleButton>
            </ToggleButtonGroup>
        </SectionFilterHeader>
    );
}

export default SkillsFilterHeader;
